import { useMemo } from 'react';
import { useTransactions, Transaction } from './useTransactions';

export type TimePeriod = 'week' | 'month' | '3months' | '6months' | 'year';

export interface CategoryDistribution {
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  total: number;
  percentage: number;
  transactionCount: number;
  color: string;
}

interface UseTransactionDistributionOptions {
  timePeriod: TimePeriod;
  transactionType: 'income' | 'expense';
}

// Time period configurations
const TIME_PERIOD_DAYS: Record<TimePeriod, number> = {
  week: 7,
  month: 30,
  '3months': 90,
  '6months': 180,
  year: 365,
};

// Color palette for categories
const CATEGORY_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#6366f1', // indigo
];

// Calculate date range from time period
const getDateRange = (timePeriod: TimePeriod): { dateFrom: string; dateTo: string } => {
  const today = new Date();
  const days = TIME_PERIOD_DAYS[timePeriod];
  const dateFrom = new Date(today);
  dateFrom.setDate(today.getDate() - days);

  return {
    dateFrom: dateFrom.toISOString().split('T')[0],
    dateTo: today.toISOString().split('T')[0],
  };
};

// Aggregate transactions by category
const aggregateByCategory = (
  transactions: Transaction[],
  transactionType: 'income' | 'expense'
): CategoryDistribution[] => {
  // Filter by transaction type
  const filteredTransactions = transactions.filter((tx) => tx.type === transactionType);

  if (filteredTransactions.length === 0) {
    return [];
  }

  // Group by category
  const categoryMap = new Map<
    string,
    {
      name: string;
      icon: string;
      total: number;
      count: number;
    }
  >();

  filteredTransactions.forEach((tx) => {
    const categoryId = tx.category_id || 'uncategorized';
    const categoryName = tx.categories?.name || 'Uncategorized';
    const categoryIcon = tx.categories?.icon || '📦';

    const existing = categoryMap.get(categoryId);
    if (existing) {
      existing.total += tx.amount;
      existing.count += 1;
    } else {
      categoryMap.set(categoryId, {
        name: categoryName,
        icon: categoryIcon,
        total: tx.amount,
        count: 1,
      });
    }
  });

  // Calculate total for percentage calculation
  const grandTotal = Array.from(categoryMap.values()).reduce(
    (sum, cat) => sum + cat.total,
    0
  );

  // Convert to CategoryDistribution array
  const distributions: CategoryDistribution[] = [];
  let colorIndex = 0;

  categoryMap.forEach((value, categoryId) => {
    distributions.push({
      categoryId,
      categoryName: value.name,
      categoryIcon: value.icon,
      total: value.total,
      percentage: grandTotal > 0 ? (value.total / grandTotal) * 100 : 0,
      transactionCount: value.count,
      color: CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length],
    });
    colorIndex++;
  });

  // Sort by total descending
  distributions.sort((a, b) => b.total - a.total);

  return distributions;
};

// Cache key for sessionStorage
const getCacheKey = (timePeriod: TimePeriod, transactionType: 'income' | 'expense'): string => {
  return `transaction_distribution_${timePeriod}_${transactionType}`;
};

// Load cached distribution from sessionStorage
const loadCachedDistribution = (
  timePeriod: TimePeriod,
  transactionType: 'income' | 'expense'
): CategoryDistribution[] | null => {
  try {
    const cacheKey = getCacheKey(timePeriod, transactionType);
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const now = Date.now();
    const cacheAge = now - parsed.timestamp;

    // Cache valid for 5 minutes
    if (cacheAge < 5 * 60 * 1000) {
      return parsed.data;
    }

    // Clear expired cache
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error('Error loading cached distribution:', error);
    return null;
  }
};

// Save distribution to sessionStorage
const saveCachedDistribution = (
  timePeriod: TimePeriod,
  transactionType: 'income' | 'expense',
  data: CategoryDistribution[]
): void => {
  try {
    const cacheKey = getCacheKey(timePeriod, transactionType);
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error('Error saving distribution to cache:', error);
  }
};

/**
 * Hook to aggregate transactions by category for a specific time period
 * Implements caching for current time period
 * Returns CategoryDistribution array with loading and error states
 */
export function useTransactionDistribution(
  options: UseTransactionDistributionOptions
): {
  data: CategoryDistribution[];
  isLoading: boolean;
  error: Error | null;
} {
  const { timePeriod, transactionType } = options;

  // Calculate date range
  const { dateFrom, dateTo } = useMemo(() => getDateRange(timePeriod), [timePeriod]);

  // Fetch transactions for the date range
  const { data: transactions, isLoading, error } = useTransactions({
    dateFrom,
    dateTo,
  });

  // Aggregate transactions by category with caching
  const distribution = useMemo(() => {
    if (!transactions) return [];

    // Check cache first
    const cached = loadCachedDistribution(timePeriod, transactionType);
    if (cached) {
      return cached;
    }

    // Aggregate transactions
    const aggregated = aggregateByCategory(transactions, transactionType);

    // Save to cache
    saveCachedDistribution(timePeriod, transactionType, aggregated);

    return aggregated;
  }, [transactions, timePeriod, transactionType]);

  return {
    data: distribution,
    isLoading,
    error: error as Error | null,
  };
}
